// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The drawn cabinet: two flipper buttons and the plunger's column (M3-49, M3-50, M3-51, M9-31).
//
// **Buttons, because a cabinet has buttons.** 08 §5.2 used to say the flipper zones are "not drawn
// as buttons — a real cabinet has no on-screen buttons", and the half of that sentence that is true
// is *on-screen*: a real cabinet has no buttons on the **glass**, and it very much has two on the
// **side rails**, under the player's fingers, where they are the most obvious objects on the
// machine. This band is not the glass — it is the cabinet below it — so the rule that kept two grey
// rectangles off the playfield never applied here.
//
// **Its own file because there are two surfaces now, and there were nearly two cabinets.** It began
// inside `MachineStage`, which was right while `/play` was the only place a machine ran. The
// Builder's test play runs the same core at the same rate through the same renderer and had **no
// controls at all** on a device without a keyboard — an author on a phone could open their machine,
// watch the ball drain, and never reach a flipper (M9-31). The fix is this band under that canvas
// too, and the one thing it must not be is a second copy: the bats' angles come from the machine's
// own geometry, the rectangles come from `phone.ts`, and both would drift apart within a milestone
// if they were written down twice.
//
// **Positioned from `phone.ts`, not by flex.** The flex version was already lying in landscape: two
// `flex: 1 1 0` children drew the divider at half the width while the hit zones are the outer
// thirds, so the visible boundary was 140 px from the real one — invisible while the zones were
// invisible, and a button in the wrong place the moment they are not.
//
// **`aria-hidden`, and spans rather than buttons.** These are inert paint: a screen-reader user is
// told about the plunger button and plays with the keyboard. See the note on `CONTROL_SELECTOR` in
// `touch.ts` for what making them real `button` elements would silently do — a touch that begins on
// a control is the control's, and the listener returns before registering it, so the flippers would
// work with a mouse and do nothing under a thumb.

import { type CSSProperties, type RefObject } from 'react'
import { batWidthFor, type Bat as BatShape, type CabinetBats } from '../cabinet'
import type { Orientation, PhoneLayout, Rect } from '../phone'
import type { ControlState } from '../machineRuntime'

export interface CabinetBandProps {
  /**
   * The band element, written to directly by `paintCabinetBand`.
   *
   * **Not React state**, because a bat goes up and down several times a second and the knob follows
   * a thumb continuously: routing either through `setState` re-renders the host — which is a large
   * component in both cases — at the rate a hand moves.
   */
  readonly bandRef: RefObject<HTMLDivElement | null>
  /** The rectangles a thumb is tested against, so the drawing cannot disagree with them. */
  readonly layout: PhoneLayout
  /** How the *host* is laid out, which decides whether the column overlaps the right zone. */
  readonly orientation: Orientation
  /** The bats, from the machine's own flipper geometry. */
  readonly bats: CabinetBats
  /** Show the words and the brighter edge — a player's first ball (08 §5.2). */
  readonly hinted: boolean
  /** Run the arrow that teaches the pull (M3-51). Its own flag, so a flip cannot spend it. */
  readonly teaching: boolean
  /** What the plunger's column says under the knob. */
  readonly pullWord: string
}

/**
 * The cabinet, drawn.
 *
 * The class names stay `stage__*` on purpose: they are the cabinet's appearance and they live in
 * `shell.css` with the rest of it, and a second set of names for the same paint is the drift this
 * file exists to prevent. The Builder gives the band a host of its own to sit in and changes
 * nothing about how it looks — an author tuning a flipper has to be pressing the button the player
 * will press.
 */
export function CabinetBand(props: CabinetBandProps): React.JSX.Element {
  const { bandRef, layout, orientation, bats, hinted, teaching, pullWord } = props
  return (
    <div
      ref={bandRef}
      className={[
        'stage__zones',
        hinted ? 'stage__zones--hinted' : '',
        // The arrow lives on its own flag, so a flipper press cannot spend the plunger lesson.
        teaching ? 'stage__zones--teach' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-hidden="true"
      // The plunger column's measured width, for the one rule that has to know it: in landscape
      // the column overlaps the right zone, and the right button steps aside by exactly this
      // much. A number in the stylesheet would be a second opinion about `phone.ts`'s arithmetic.
      // React never removes `--draw` alongside it — that property is written by `paintCabinetBand`
      // and has never been in a React style object, so the diff does not know to take it away.
      style={
        {
          ['--lane']: `${layout.plunger.width}px`,
          // One width for both bats, from the narrower zone — see `batWidthFor`.
          ['--bat-size']: `${batWidthFor(
            layout.left.width,
            layout.right.width,
            orientation === 'landscape' ? layout.plunger.width : 0,
          )}px`,
        } as CSSProperties
      }
    >
      {/*
        The bats' finish, as gradients the two buttons both reference (M3-50).

        A zero-sized SVG holding only `<defs>`, because `fill: url(#id)` is resolved against the
        document and a paint server has to exist somewhere in it. Here rather than in `index.html`
        so it arrives and leaves with the machine, and `aria-hidden` because it draws nothing.

        **Two bands are never on screen at once** — `/play` is a route and the Builder's test play
        replaces the workspace — so the duplicate ids a second band would mint are not reachable.
      */}
      <svg className="stage__bat-paint" aria-hidden="true" focusable="false">
        <defs>
          {/*
            Bright enough to be an object on a black cabinet (M3-50). The first version ran
            #33436b to #0e1526, which is a reasonable button on a page and almost invisible here:
            measured against the band's `background: #000`, the bat read as an outline with
            nothing inside it. A real bat is one of the most visible things on a playfield.
          */}
          <linearGradient id="batFace" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#6c80ac" />
            <stop offset="42%" stopColor="#3c4c74" />
            <stop offset="100%" stopColor="#1b2440" />
          </linearGradient>
          <linearGradient id="batFaceLit" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#9fe9ff" />
            <stop offset="45%" stopColor="#4fd8ff" />
            <stop offset="100%" stopColor="#1c7fa4" />
          </linearGradient>
        </defs>
      </svg>
      <span className="stage__zone stage__zone--left" style={placeInBand(layout, layout.left)}>
        <Bat bat={bats.left} />
        <span className="stage__button-word">LEFT</span>
      </span>
      <span className="stage__zone stage__zone--right" style={placeInBand(layout, layout.right)}>
        <Bat bat={bats.right} />
        <span className="stage__button-word">RIGHT</span>
      </span>
      {/*
        The plunger, as a plunger: a knob at the top of its column and a rod that pays out behind
        it as the thumb drags down. Always rendered, live only while a ball waits — the housing is
        where a player learns the plunger lives, and a control that appears out of nowhere when a
        ball is served is one nobody is looking for. `--draw` and the live class are written
        straight onto the band by `paintCabinetBand`, at the rate a thumb moves rather than at the
        readout's twenty steps.
      */}
      <span className="stage__plunger" style={placeInBand(layout, layout.plunger)}>
        {/* Shaft first, ball second: the ball is the end of the shaft and has to paint over it. */}
        <span className="stage__plunger-shaft" />
        <span className="stage__plunger-ball">
          {/* Moulded into the ball rather than laid on top of it — a marking, not a UI chevron —
              because it is the only thing on screen that is permanently saying which way this
              goes, and the ball has to stay a ball. */}
          <span className="stage__plunger-mark">▼</span>
        </span>
        {/*
          The arrow that teaches the pull (M3-51), and the only thing on this machine that ever
          demonstrates a gesture rather than naming one. Below the seated ball and moving the way
          the ball has to move; retired for good on the first real pull, per browser.
        */}
        <span className="stage__plunger-arrow" aria-hidden="true">
          <span className="stage__plunger-chevron">⌄</span>
          <span className="stage__plunger-chevron">⌄</span>
        </span>
        <span className="stage__plunger-word">{pullWord}</span>
      </span>
    </div>
  )
}

/**
 * Write a frame of control state straight onto the band (M3-49).
 *
 * Two classes and a custom property, at the rate a hand moves. The host hands this to the runtime
 * as `onControls`, which is told only when something changed — a different rate from the readout,
 * and the reason neither of these is React state.
 */
export function paintCabinetBand(band: HTMLElement | null, state: ControlState): void {
  if (band === null) return
  band.classList.toggle('stage__zones--left', state.left)
  band.classList.toggle('stage__zones--right', state.right)
  band.classList.toggle('stage__zones--live', state.plungerLive)
  // A thumb is on the plunger right now (M3-51). `draw` is zero unless something is drawing it,
  // and the floor for a touch is `PLUNGE_MIN` — so this is "being pulled", not "pulled far".
  // The teaching arrow stops the moment it does: an animation demonstrating a gesture the player
  // is in the middle of performing is a machine that has not noticed.
  band.classList.toggle('stage__zones--drawing', state.draw > 0)
  band.style.setProperty('--draw', state.draw.toFixed(3))
}

/**
 * One flipper button, drawn as the bat it drives (M3-50).
 *
 * **Inline SVG, and it is not an asset.** The content rule is zero image files; this is a path
 * computed from the machine's own `length`, `radiusPivot` and `radiusTip` at render time, which is
 * as generated-in-code as the icons the manifest uses. Nothing is fetched and nothing is a file.
 *
 * `aria-hidden` and `focusable="false"`: it lives inside the band, which is already hidden from
 * the accessibility tree, and an SVG that IE-era focus rules can tab to would put a stop on a
 * keyboard player's way through the page for a decoration.
 *
 * **The rotations are custom properties rather than a `transform` here**, so the stylesheet owns
 * *when* the bat is at rest and when it is held, and this owns *what those angles are*. The two
 * questions have different answers on every machine and the same answer on every phone.
 */
function Bat({ bat }: { readonly bat: BatShape }): React.JSX.Element {
  return (
    <svg
      className="stage__button"
      viewBox={bat.viewBox}
      aria-hidden="true"
      focusable="false"
      style={
        {
          ['--bat-rest']: `${bat.restDegrees.toFixed(2)}deg`,
          ['--bat-pressed']: `${bat.pressedDegrees.toFixed(2)}deg`,
          ['--bat-pivot']: `${(bat.pivot * 100).toFixed(2)}%`,
        } as CSSProperties
      }
    >
      <path className="stage__button-bat" d={bat.path} />
    </svg>
  )
}

/**
 * Put one of `phone.ts`'s rectangles where the band can draw it (M3-49).
 *
 * The rectangles are in **surface** coordinates, because that is what the touch listener measures
 * against; the band is a box inside the surface, so drawing them needs the offset taken off. In
 * portrait the band starts at the top of the flipper zones — which is true of the Builder's stacked
 * layout as well; in landscape it is `inset: 0` over the whole stage and the offset is nothing.
 *
 * This exists so the buttons are positioned from the same arithmetic the thumb is tested against,
 * rather than from a flex rule that agrees with it on one orientation and not the other.
 */
export function placeInBand(layout: PhoneLayout, rect: Rect): CSSProperties {
  const originY = layout.orientation === 'portrait' ? layout.left.y : 0
  return {
    left: `${rect.x}px`,
    top: `${rect.y - originY}px`,
    width: `${rect.width}px`,
    height: `${rect.height}px`,
  }
}
